import React from 'react';
import { Link } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { useLanguage } from '../context/LanguageContext';
import { Home, ArrowLeft, Search } from 'lucide-react';
import { Button } from '../components/ui/button';

const NotFound = () => {
  const { isDark } = useTheme();
  const { language } = useLanguage();

  const content = {
    de: {
      title: '404',
      subtitle: 'Seite nicht gefunden',
      description: 'Die gesuchte Seite existiert leider nicht oder wurde verschoben.',
      backHome: 'Zur Startseite',
      backButton: 'Zurück',
      searchHint: 'Vielleicht finden Sie hier, was Sie suchen:',
      links: [
        { href: '/', label: 'Haartransplantation' },
        { href: '/bart', label: 'Barttransplantation' },
        { href: '/augenbrauen', label: 'Augenbrauentransplantation' },
      ]
    },
    en: {
      title: '404',
      subtitle: 'Page not found',
      description: 'The page you are looking for does not exist or has been moved.',
      backHome: 'Back to Home',
      backButton: 'Go Back',
      searchHint: 'Maybe you can find what you are looking for here:',
      links: [
        { href: '/', label: 'Hair Transplant' },
        { href: '/bart', label: 'Beard Transplant' },
        { href: '/augenbrauen', label: 'Eyebrow Transplant' },
      ]
    }
  };

  const t = content[language] || content.de;

  return (
    <div className={`min-h-screen flex items-center justify-center px-4 transition-colors duration-300 ${
      isDark ? 'bg-[#0a0a0a]' : 'bg-gray-50'
    }`}>
      {/* Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className={`absolute top-1/4 left-1/4 w-[500px] h-[500px] rounded-full blur-[120px] ${
          isDark ? 'bg-[#7BA7C2]/10' : 'bg-[#5A8BA6]/10'
        }`} />
        <div className={`absolute bottom-1/4 right-1/4 w-[400px] h-[400px] rounded-full blur-[100px] ${
          isDark ? 'bg-[#5A8BA6]/10' : 'bg-[#7BA7C2]/10'
        }`} />
      </div>

      <div className="relative text-center max-w-lg mx-auto">
        {/* 404 Number */}
        <div className="mb-8">
          <h1 className={`text-[150px] sm:text-[200px] font-bold leading-none ${
            isDark 
              ? 'text-transparent bg-clip-text bg-gradient-to-b from-white/20 to-white/5' 
              : 'text-transparent bg-clip-text bg-gradient-to-b from-gray-300 to-gray-100'
          }`}>
            {t.title}
          </h1>
        </div>

        {/* Content */}
        <div className="space-y-4 mb-8">
          <h2 className={`text-2xl sm:text-3xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
            {t.subtitle}
          </h2>
          <p className={`text-base ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
            {t.description}
          </p>
        </div>

        {/* Buttons */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-12">
          <Button
            onClick={() => window.history.back()}
            variant="outline"
            className={`flex items-center gap-2 px-6 py-3 ${
              isDark 
                ? 'border-white/10 text-white hover:bg-white/5' 
                : 'border-gray-300 text-gray-700 hover:bg-gray-100'
            }`}
          >
            <ArrowLeft className="w-4 h-4" />
            {t.backButton}
          </Button>
          
          <Link to="/">
            <Button
              className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-[#7BA7C2] to-[#5A8BA6] hover:from-[#5A8BA6] hover:to-[#7BA7C2] text-white"
            >
              <Home className="w-4 h-4" />
              {t.backHome}
            </Button>
          </Link>
        </div>

        {/* Quick Links */}
        <div className={`p-6 rounded-2xl border ${
          isDark ? 'bg-white/[0.02] border-white/10' : 'bg-white border-gray-200'
        }`}>
          <div className="flex items-center justify-center gap-2 mb-4">
            <Search className={`w-4 h-4 ${isDark ? 'text-[#7BA7C2]' : 'text-[#5A8BA6]'}`} />
            <span className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
              {t.searchHint}
            </span>
          </div>
          <div className="flex flex-wrap justify-center gap-2">
            {t.links.map((link, index) => (
              <Link
                key={index}
                to={link.href}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 ${
                  isDark 
                    ? 'bg-white/5 text-gray-300 hover:bg-[#7BA7C2]/20 hover:text-[#7BA7C2]' 
                    : 'bg-gray-100 text-gray-700 hover:bg-[#5A8BA6]/10 hover:text-[#5A8BA6]'
                }`}
              >
                {link.label}
              </Link>
            ))}
          </div>
        </div>

        {/* Logo */}
        <div className="mt-12">
          <Link 
            to="/" 
            className={`text-lg tracking-wide transition-colors duration-300 ${
              isDark ? 'text-gray-500 hover:text-[#7BA7C2]' : 'text-gray-400 hover:text-[#5A8BA6]'
            }`}
            style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 100 }}
          >
            Dion Hair Clinic
          </Link>
        </div>
      </div>
    </div>
  );
};

export default NotFound;
